import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MessageCircle } from "lucide-react";
import { whatsappLink } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const subscribe = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return toast.error("Please enter a valid email");
    setSubscribing(true);
    setTimeout(() => {
      toast.success("Thank you — you're on the list.");
      setEmail("");
      setSubscribing(false);
    }, 700);
  };

  return (
    <footer data-testid="v2-footer" className="mt-24 bg-[#1a1a1a] text-[#fbf9f5]">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline-v2 text-[#c9ac93]">Stay in touch</div>
            <h3 className="mt-4 font-serif-warm text-4xl md:text-5xl leading-[1.1] tracking-tight max-w-lg">
              Stories, Spaces and New Collections.
            </h3>
            <p className="mt-5 text-[15px] text-white/70 max-w-md font-light">
              Receive new collection updates, styling inspiration and stories from the world of handmade décor.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex gap-3 max-w-md w-full">
            <input
              data-testid="v2-newsletter-email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent border-b border-white/40 focus:border-white text-[15px] py-3 px-1 outline-none placeholder:text-white/40"
            />
            <button
              type="submit"
              data-testid="v2-newsletter-submit"
              disabled={subscribing}
              className="text-[11px] uppercase tracking-[0.24em] font-medium border-b border-white pb-3 hover:text-[#c9ac93] hover:border-[#c9ac93] disabled:opacity-60"
            >
              {subscribing ? "..." : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-14 grid grid-cols-2 md:grid-cols-12 gap-8">
        <div className="col-span-2 md:col-span-4">
          <div className="font-serif-warm text-3xl">Iryaa Homes</div>
          <p className="mt-4 text-[14px] text-white/70 max-w-xs font-light leading-relaxed">
            Handcrafted rugs and home décor, made by skilled Indian artisans for homes that value warmth, texture and story.
          </p>
          <div className="mt-6 flex gap-3">
            <a href="https://instagram.com/iryaa_homes" target="_blank" rel="noreferrer" aria-label="Instagram" className="w-9 h-9 border border-white/30 flex items-center justify-center hover:border-white hover:text-[#c9ac93]"><Instagram size={15} /></a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" className="w-9 h-9 border border-white/30 flex items-center justify-center hover:border-white hover:text-[#c9ac93]"><Facebook size={15} /></a>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 border border-white/30 flex items-center justify-center hover:border-white hover:text-[#c9ac93]"><MessageCircle size={15} /></a>
            <a href="mailto:info@iryaahomes.in" aria-label="Email" className="w-9 h-9 border border-white/30 flex items-center justify-center hover:border-white hover:text-[#c9ac93]"><Mail size={15} /></a>
          </div>
        </div>

        {[
          { title: "Shop", items: [
            { l: "Hand-Tufted Rugs", to: "/collections?cat=carpets" },
            { l: "Runner Rugs", to: "/collections?cat=carpets" },
            { l: "Jute Rugs", to: "/collections?cat=rugs" },
            { l: "Custom Rugs", to: "/custom" },
            { l: "Wall Décor", to: "/collections?cat=photo-frames" },
            { l: "Bags & Baskets", to: "/collections?cat=kids-baskets" },
          ]},
          { title: "The Studio", items: [
            { l: "Our Story", to: "/about" },
            { l: "Journal", to: "/journal" },
            { l: "Contact", to: "/contact" },
            { l: "Rug Care", to: "/journal" },
            { l: "Trade Enquiries", to: "/contact" },
          ]},
          { title: "Support", items: [
            { l: "FAQs", to: "/faqs" },
            { l: "Shipping Policy", to: "/policies/shipping" },
            { l: "Returns & Refunds", to: "/policies/refunds" },
            { l: "Privacy Policy", to: "/policies/privacy" },
            { l: "Terms", to: "/policies/terms" },
          ]},
        ].map((col) => (
          <div key={col.title} className="col-span-1 md:col-span-2 md:col-start-auto">
            <div className="overline-v2 text-[#c9ac93] mb-4">{col.title}</div>
            <ul className="space-y-2.5 text-[13px] text-white/75">
              {col.items.map((it) => (
                <li key={it.l}><Link to={it.to} className="v2-link hover:text-white">{it.l}</Link></li>
              ))}
            </ul>
          </div>
        ))}

        <div className="col-span-2 md:col-span-2">
          <div className="overline-v2 text-[#c9ac93] mb-4">Studio</div>
          <address className="not-italic text-[13px] text-white/70 leading-relaxed font-light">
            17, Lavelle Lane<br/>
            Bengaluru 560 001<br/>
            +91 89711 88977<br/>
            info@iryaahomes.in
          </address>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between gap-3 text-[11px] text-white/50">
          <div>© {new Date().getFullYear()} Iryaa Homes · Handmade in India</div>
          <div className="tracking-[0.24em] uppercase">Secure payments · Razorpay</div>
        </div>
      </div>
    </footer>
  );
}
